// src/features/userprofile/logic.ts
// Profile validation, avatar helpers, settings transforms.
// Framework-agnostic — NO React imports.

import type { ProfileEditFormState } from './types';

// ============================================
// Validation
// ============================================

export function validateProfile(data: ProfileEditFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.firstName.trim()) errors.firstName = 'userprofile.firstNameRequired';
  if (!data.lastName.trim()) errors.lastName = 'userprofile.lastNameRequired';
  if (data.bio.length > 256) errors.bio = 'userprofile.bioTooLong';
  if (data.displayName && data.displayName.length > 64) errors.displayName = 'userprofile.displayNameTooLong';
  return errors;
}

export function validateAvatar(file: File): Record<string, string> {
  const errors: Record<string, string> = {};
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  if (file.size > maxSize) errors.file = 'userprofile.avatarTooLarge';
  if (!allowed.includes(file.type)) errors.file = 'userprofile.avatarInvalidType';
  return errors;
}

export function validateBanner(file: File): Record<string, string> {
  const errors: Record<string, string> = {};
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  if (file.size > maxSize) errors.file = 'userprofile.bannerTooLarge';
  if (!allowed.includes(file.type)) errors.file = 'userprofile.bannerInvalidType';
  return errors;
}

export const bannerGradients = [
  { value: 'from-primary/20 via-primary/10 to-accent/20', labelKey: 'userprofile.gradientDefault' },
  { value: 'from-blue-500/20 via-cyan-400/10 to-teal-400/20', labelKey: 'userprofile.gradientOcean' },
  { value: 'from-purple-500/20 via-pink-400/10 to-rose-400/20', labelKey: 'userprofile.gradientSunset' },
  { value: 'from-emerald-500/20 via-green-400/10 to-lime-400/20', labelKey: 'userprofile.gradientForest' },
  { value: 'from-amber-500/20 via-orange-400/10 to-red-400/20', labelKey: 'userprofile.gradientWarm' },
  { value: 'from-slate-500/20 via-gray-400/10 to-zinc-400/20', labelKey: 'userprofile.gradientNeutral' },
] as const;

// ============================================
// Profile status map
// ============================================

export const profileStatusMap: Record<string, { labelKey: string; variant: string }> = {
  PROFILE_STATUS_UNSPECIFIED: { labelKey: 'userprofile.statusUnspecified', variant: 'secondary' },
  PROFILE_STATUS_ACTIVE: { labelKey: 'userprofile.statusActive', variant: 'success' },
  PROFILE_STATUS_INACTIVE: { labelKey: 'userprofile.statusInactive', variant: 'secondary' },
  PROFILE_STATUS_SUSPENDED: { labelKey: 'userprofile.statusSuspended', variant: 'warning' },
  PROFILE_STATUS_BANNED: { labelKey: 'userprofile.statusBanned', variant: 'error' },
  PROFILE_STATUS_PENDING_VERIFICATION: { labelKey: 'userprofile.statusPending', variant: 'info' },
  PROFILE_STATUS_DELETED: { labelKey: 'userprofile.statusDeleted', variant: 'error' },
};

export function formatProfileStatus(status: string): { labelKey: string; variant: string } {
  return profileStatusMap[status] ?? profileStatusMap.PROFILE_STATUS_UNSPECIFIED;
}

// ============================================
// Profile visibility options
// ============================================

export const profileVisibilityOptions = [
  { value: 'PROFILE_VISIBILITY_PUBLIC', labelKey: 'userprofile.visibilityPublic' },
  { value: 'PROFILE_VISIBILITY_AUTHENTICATED', labelKey: 'userprofile.visibilityAuthenticated' },
  { value: 'PROFILE_VISIBILITY_CONNECTIONS', labelKey: 'userprofile.visibilityConnections' },
  { value: 'PROFILE_VISIBILITY_PRIVATE', labelKey: 'userprofile.visibilityPrivate' },
] as const;

// ============================================
// UI setting choices
// ============================================

export const themeOptions = [
  { value: 'light', labelKey: 'userprofile.themeLight' },
  { value: 'dark', labelKey: 'userprofile.themeDark' },
  { value: 'system', labelKey: 'userprofile.themeSystem' },
] as const;

export const accentColorOptions = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
] as const;

export const densityOptions = [
  { value: 'comfortable', labelKey: 'userprofile.densityComfortable' },
  { value: 'compact', labelKey: 'userprofile.densityCompact' },
] as const;

export const languageOptions = [
  { value: 'en', labelKey: 'userprofile.langEnglish' },
  { value: 'ru', labelKey: 'userprofile.langRussian' },
  { value: 'zh', labelKey: 'userprofile.langChinese' },
] as const;

export const timezoneOptions = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0/+1)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1/+2)' },
  { value: 'America/New_York', label: 'America/New_York (UTC-5/-4)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8/-7)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
] as const;

// ============================================
// Accent color → HSL conversion
// ============================================

/**
 * Convert hex color (#RRGGBB) to HSL string "H S% L%" for CSS variables.
 */
export function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '222.2 47.4% 11.2%'; // fallback to default primary
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Generate a foreground color (light or dark) that contrasts with the given HSL primary.
 */
export function accentForeground(hsl: string): string {
  const parts = hsl.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return '210 40% 98%';
  const l = parseFloat(parts[2]);
  return l > 50 ? '222.2 47.4% 11.2%' : '210 40% 98%';
}

// ============================================
// Profile helpers
// ============================================

export function getProfileInitials(firstName: string, lastName: string): string {
  return `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase();
}

export const genderOptions = [
  { value: '', labelKey: 'userprofile.genderUnspecified' },
  { value: 'male', labelKey: 'userprofile.genderMale' },
  { value: 'female', labelKey: 'userprofile.genderFemale' },
  { value: 'other', labelKey: 'userprofile.genderOther' },
  { value: 'prefer_not_to_say', labelKey: 'userprofile.genderPreferNot' },
] as const;


// ============================================
// Ban formatter
// ============================================

export function formatProfileBan(ban: {
  reason?: string;
  banned_at?: string;
  expires_at?: string;
}): { reason: string; bannedAt: string; expiresAt: string; isPermanent: boolean } {
  return {
    reason: ban.reason ?? '',
    bannedAt: ban.banned_at ?? '',
    expiresAt: ban.expires_at ?? '',
    isPermanent: !ban.expires_at,
  };
}
